import { SetStateAction } from "react";
import styles from "./styles/FilesFoldersBlock.module.scss";
import classNames from "classnames";
import FontAwesome from "../../../utilities/FontAwesome";
import FilesListHeader from "./FilesListHeader";
import CleverTable from "../../../utilities/CleverTable/CleverTable";
import ImageComponent from "../../../utilities/ImageComponent";
import { getIconByMimeType, getIconSet } from "./fileUtilities";
import moment from "moment";
import { observer } from "mobx-react";
import { EditButton } from "../EditButton";
import { canPreviewContentItem } from "./FilesFoldersBlock";
import {
  IReactiveFileContentItem,
  IReactiveFolderContentItem,
} from "../../../schoolfeed/SchoolFeedContentItem";
import { useStores } from "../../../../stores/useStores";
import type { AnalyticsEventName } from "../../../../context/AnalyticsContext";
import { useAnalyticsSafe } from "../../../../context/AnalyticsContext";
import ClickableLink from "../../../utilities/ClickableLink";
import { Box, Button, Text } from "@radix-ui/themes";
import { getPlatform, getSourceTitle } from "../../../../utils/StringUtilities";

const transitionClassNames = {
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  enterDone: styles.transitionEnterDone,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
  exitDone: styles.transitionExitDone,
};

const FilesList = observer(
  (props: {
    files_folders: Array<IReactiveFileContentItem | IReactiveFolderContentItem>;
    setSelectedContentItem?: SetStateAction<any>;
    setPreviewIsExpanded?: SetStateAction<any>;
    previewIsExpanded?: boolean;
    selectedItem?: FileContentItem | FolderContentItem;
    aboveTheFold: boolean;
    handleFetchMore?: () => void;
    shouldFetchMore?: boolean;
    maxHeight?: number | string;
    useInfiniteScroll?: boolean;
    handleDelete?: (contentItem: ContentItem) => void;
    handleFolderClick?: (folder: FolderContentItem) => void;
    handleUpwardsNavigation?: () => void;
    upwardsNavigationButtonText?: string;
    loading?: boolean;
  }) => {
    const { organizationStore, modalStore, interfaceStore, userStore } =
      useStores();
    const { searchContext } = useAnalyticsSafe();

    const filesContainerClassName = classNames({
      [styles.filesSliderContainer]: true,
    });
    const expandButtonClassName = classNames({
      [styles.expandPreviewButton]: true,
      [styles.expandPreviewButtonNotExpanded]: !props.previewIsExpanded,
    });

    const expandButtonContainerClassName = classNames({
      [styles.expandedPreviewButtonContainer]: true,
      [styles.expandedPreviewButtonContainerNotEditor]: !userStore.isEditor,
    });

    const columns = [
      {
        title: <FilesListHeader name={"Name"} />,
        dataKey: "name",
        initialWidth: 100,
      },
    ];

    return (
      <CleverTable
        columns={columns}
        transitionIn={props.aboveTheFold}
        useInfiniteScroll={props.useInfiniteScroll}
        transitionClassNames={transitionClassNames}
        data={[
          ...(props.upwardsNavigationButtonText
            ? [
                {
                  key: "upwardsNavigation",
                  name: (
                    <div className={styles.tableCell}>
                      <div className={styles.titleContainer}>
                        <FontAwesome
                          prefix={"fas"}
                          name={"fa-turn-up"}
                          flip={"horizontal"}
                          fixedWidth
                        />
                        <span>{props.upwardsNavigationButtonText}</span>
                      </div>
                    </div>
                  ),
                  onClick: props.handleUpwardsNavigation,
                  height: 50,
                },
              ]
            : []),
          ...props.files_folders.map((file, idx) => {
            const fileOrigin = getIconSet(file.type);
            const fileOrFolderSettings = file.is_file
              ? (file as FileContentItem).json_data.file
              : (file as FolderContentItem).json_data.folder;
            const iconUrl = getIconByMimeType(
              fileOrigin,
              file.is_folder
                ? "application/vnd.google-apps.folder"
                : file.json_data.file?.mime_type,
            );
            const shouldOpenPreview = canPreviewContentItem(
              file,
              organizationStore.organization,
            );
            const shouldOpenFolder = file.is_folder && props.handleFolderClick;
            const tableCellClassName = classNames({
              [styles.tableCell]: true,
              [styles.tableCellHidden]: !file.published,
            });
            const viewLink = fileOrFolderSettings?.view_link;

            let onClick;
            if (shouldOpenPreview) {
              onClick = () => {
                if (
                  interfaceStore.dimensions.width < 1000 ||
                  !props.setSelectedContentItem
                ) {
                  modalStore.addModal({
                    type: "file-preview",
                    id: file.third_party_id,
                    itemObj: file,
                  });
                } else {
                  props.setSelectedContentItem(file);
                  if (
                    file.third_party_id !== props.selectedItem?.third_party_id
                  ) {
                    props.setPreviewIsExpanded(false);
                  }
                }
              };
            } else if (shouldOpenFolder) {
              onClick = () => {
                if (userStore.id) {
                  props.handleFolderClick?.(file as FolderContentItem);
                } else {
                  const platform = getSourceTitle(getPlatform(file.type));
                  modalStore.addModal({
                    type: "login",
                    RenderChildrenNode: (props) => {
                      return (
                        <>
                          <Box>
                            <Text size={"3"} weight={"medium"}>
                              Login to navigate folder contents in site.
                            </Text>
                          </Box>
                          {props.children}
                          <Box px={"4"} mb={"2"}>
                            <Text size={"3"} weight={"medium"}>
                              Don&#39;t want to login?
                              <Box style={{ textAlign: "center" }} pt={"2"}>
                                <Button asChild>
                                  <ClickableLink
                                    href={viewLink}
                                    style={{ color: "white" }}
                                  >
                                    View in {platform}
                                    <FontAwesome
                                      name={"fa-external-link-alt"}
                                      prefix={"fas"}
                                    />
                                  </ClickableLink>
                                </Button>
                              </Box>
                            </Text>
                          </Box>
                        </>
                      );
                    },
                  });
                }
              };
            }

            return {
              name: (
                <div className={tableCellClassName}>
                  <ImageComponent src={iconUrl} />
                  <div
                    className={classNames(styles.titleContainer, {
                      [styles.titleContainerSelected]:
                        props.selectedItem?.third_party_id ===
                        file.third_party_id,
                    })}
                  >
                    {shouldOpenPreview || shouldOpenFolder ? (
                      <span>{file.title}</span>
                    ) : (
                      <ClickableLink href={viewLink}>
                        <span>{file.title}</span>
                        <FontAwesome
                          name={"fa-external-link-alt"}
                          prefix={"fas"}
                        />
                      </ClickableLink>
                    )}
                  </div>
                  {!file?.isExternalGoogleResult &&
                    props.selectedItem?.third_party_id ===
                      file.third_party_id && (
                      <div className={expandButtonContainerClassName}>
                        <button
                          className={expandButtonClassName}
                          onClick={
                            props.previewIsExpanded
                              ? () => props.setPreviewIsExpanded(false)
                              : () => props.setPreviewIsExpanded(true)
                          }
                        >
                          <FontAwesome
                            name={
                              props.previewIsExpanded
                                ? "fa-down-left-and-up-right-to-center"
                                : "fa-up-right-and-down-left-from-center"
                            }
                            prefix={"fas"}
                          />{" "}
                          {props.previewIsExpanded ? "Shrink" : "Expand"}
                        </button>
                      </div>
                    )}
                  {userStore.isEditor && file.id && props.handleDelete && (
                    <div className={styles.editButton}>
                      <EditButton
                        contentItem={file}
                        handleDelete={props.handleDelete}
                      />
                    </div>
                  )}
                  <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: file.description }}
                  />
                </div>
              ),
              modified: (
                <div className={tableCellClassName}>
                  {file.is_file ? (
                    moment(
                      file.is_custom
                        ? file.updated_at
                        : fileOrFolderSettings.updated_at,
                    ).fromNow()
                  ) : (
                    <p></p>
                  )}
                </div>
              ),
              onClick,
              analyticsEvent: (shouldOpenPreview && searchContext
                ? "search_result_clicked"
                : undefined) as AnalyticsEventName | undefined,
              analyticsProperties:
                shouldOpenPreview && searchContext
                  ? {
                      result_type: "files_and_folders",
                      result_id: file.id,
                      result_title: file.title,
                    }
                  : undefined,
              key: file.third_party_id,
              style: {
                transitionDelay: `${idx * 0.15}s`,
              },
              height: file.description ? 75 : 50,
            };
          }),
        ]}
        className={filesContainerClassName}
        tableRowClassName={styles.tableRow}
        noDataMessage={"No files or folders found."}
        initialWidthIsPercent={true}
        selectedItemKey={props.selectedItem?.third_party_id}
        handleFetchMore={props.handleFetchMore}
        shouldFetchMore={props.shouldFetchMore}
        maxHeight={props.maxHeight}
        loading={props.loading}
      />
    );
  },
);

export default FilesList;
